import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
} from 'recharts';
import {
    Edit as EditIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Event as EventIcon,
    Assignment as AssignmentIcon,
    TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import AdminLayout from './AdminLayout';
import { formService } from '../services/firestoreService';
import competitionService from '../services/competitionService';

const COLORS = ['#0D47A1', '#D32F2F', '#388E3C', '#F57C00'];

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [forms, setForms] = useState([]);
    const [competitions, setCompetitions] = useState([]);
    const [competitionsToday, setCompetitionsToday] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [publishStatus, setPublishStatus] = useState({});

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError('');

            // ✅ Fetch ALL forms (including unpublished)
            const formsData = await formService.getAllForms();
            const filteredForms = Array.isArray(formsData)
                ? formsData.filter(f => f.status !== 'submitted')
                : [];
            setForms(filteredForms);

            // ✅ Initialize publish status from each form
            const statusMap = {};
            filteredForms.forEach(form => {
                statusMap[form.id] = form.isPublished || false;
            });
            setPublishStatus(statusMap);

            // ✅ Fetch competitions
            const compsData = await competitionService.getCompetitions();
            const allComps = Array.isArray(compsData) ? compsData : [];
            setCompetitions(allComps);

            // ✅ Filter competitions with result announcement TODAY
            const today = new Date().toISOString().split('T');
            const todayComps = allComps.filter(comp => {
                const resultDate = comp.resultDeclarationDate?.split('T');
                return resultDate === today;
            });
            setCompetitionsToday(todayComps);
        } catch (err) {
            console.error('Error fetching dashboard data:', err);
            setError('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const handleTogglePublish = async (formId) => {
        try {
            const newStatus = !publishStatus[formId];

            // ✅ Update form publish status in Firebase
            await formService.updateForm(formId, { isPublished: newStatus });

            // ✅ Update local state
            setPublishStatus(prev => ({
                ...prev,
                [formId]: newStatus,
            }));
        } catch (err) {
            console.error('Error updating publish status:', err);
            setError('Failed to update form status');
        }
    };

    const handleEditForm = (formId) => {
        navigate(`/forms/edit/${formId}`);
    };

    if (loading) {
        return (
            <AdminLayout title="📊 Dashboard">
                <div className="flex justify-center items-center min-h-screen">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout title="📊 Admin Dashboard">
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
                <div className="max-w-7xl mx-auto space-y-8">
                    {/* ============== ERROR ALERT ============== */}
                    {error && (
                        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex justify-between">
                            <span>{error}</span>
                            <button onClick={() => setError('')} className="font-bold">×</button>
                        </div>
                    )}

                    {/* ============== HEADER ============== */}
                    <div>
                        <h1 className="text-4xl font-bold text-gray-900">📊 Admin Dashboard</h1>
                        <p className="text-gray-600 mt-2">Manage forms, competitions, and visibility settings</p>
                    </div>

                    {/* ============== KPI CARDS ============== */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Total Forms */}
                        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-600">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-600 text-sm font-semibold">📋 Total Forms</p>
                                    <p className="text-4xl font-bold text-gray-900 mt-2">{forms.length}</p>
                                </div>
                                <AssignmentIcon className="text-blue-600 text-5xl opacity-20" />
                            </div>
                        </div>

                        {/* Published Forms */}
                        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-600">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-600 text-sm font-semibold">✅ Published</p>
                                    <p className="text-4xl font-bold text-gray-900 mt-2">
                                        {Object.values(publishStatus).filter(s => s).length}
                                    </p>
                                </div>
                                <VisibilityIcon className="text-green-600 text-5xl opacity-20" />
                            </div>
                        </div>

                        {/* Total Competitions */}
                        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-red-600">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-600 text-sm font-semibold">🏆 Competitions</p>
                                    <p className="text-4xl font-bold text-gray-900 mt-2">{competitions.length}</p>
                                </div>
                                <TrendingUpIcon className="text-red-600 text-5xl opacity-20" />
                            </div>
                        </div>
                    </div>

                    {/* ============== COMPETITIONS TODAY ============== */}
                    {competitionsToday.length > 0 && (
                        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-600">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <EventIcon className="text-orange-600" />
                                🎯 Results Announced Today ({competitionsToday.length})
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {competitionsToday.map(comp => (
                                    <div key={comp.id} className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                                        <h3 className="font-bold text-gray-900">{comp.title}</h3>
                                        <p className="text-sm text-gray-600 mt-1">{comp.description}</p>
                                        <div className="flex items-center gap-2 mt-2">
                                            <span className="px-3 py-1 bg-orange-200 text-orange-800 text-xs rounded-full font-semibold">
                                                🔔 Results Today
                                            </span>
                                            <span className="text-sm text-gray-600">
                                                {comp.startupIds?.length || 0} startups
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ============== FORM VISIBILITY CONTROL ============== */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">🔒 Form Visibility Control</h2>
                        <p className="text-gray-600 mb-6">
                            ℹ️ Control which forms are visible to students. Toggle "Show/Hide" to control visibility.
                        </p>

                        {forms.length > 0 ? (
                            <div className="space-y-3">
                                {forms.map(form => (
                                    <div
                                        key={form.id}
                                        className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1">
                                                <h3 className="font-semibold text-gray-900">{form.title}</h3>
                                                <p className="text-sm text-gray-600 mt-1">{form.description}</p>
                                                <div className="flex items-center gap-2 mt-2">
                                                    <span className={`px-3 py-1 text-xs rounded-full font-semibold ${publishStatus[form.id]
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-gray-100 text-gray-800'
                                                        }`}>
                                                        {publishStatus[form.id] ? '✅ Published' : '❌ Draft'}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-2">
                                                {/* Toggle Button */}
                                                <button
                                                    onClick={() => handleTogglePublish(form.id)}
                                                    className={`px-4 py-2 rounded-lg font-semibold transition flex items-center gap-2 ${publishStatus[form.id]
                                                        ? 'bg-green-600 text-white hover:bg-green-700'
                                                        : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                                                        }`}
                                                >
                                                    {publishStatus[form.id] ? (
                                                        <>
                                                            <VisibilityIcon /> Hide
                                                        </>
                                                    ) : (
                                                        <>
                                                            <VisibilityOffIcon /> Show
                                                        </>
                                                    )}
                                                </button>

                                                {/* Edit Button */}
                                                <button
                                                    onClick={() => handleEditForm(form.id)}
                                                    className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition"
                                                    title="Edit form"
                                                >
                                                    <EditIcon />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center p-8 text-gray-500">
                                No forms available. Create a form to get started.
                            </div>
                        )}
                    </div>

                    {/* ============== GUIDANCE SECTION ============== */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                        <h3 className="text-lg font-bold text-blue-900 mb-3">📖 How to Control Form Visibility</h3>
                        <ul className="space-y-2 text-sm text-blue-800">
                            <li>✅ <strong>Published</strong> forms appear in Student Dashboard under "Available Forms"</li>
                            <li>❌ <strong>Draft</strong> forms are hidden from students</li>
                            <li>🔄 Toggle "Show/Hide" button to publish or unpublish forms instantly</li>
                            <li>✏️ Use the "Edit" button to modify form details</li>
                            <li>💡 Published forms should be ready for students to complete</li>
                        </ul>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
}
